import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Pagination,
  Chip,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  QrCodeScanner as ScannerIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import {
  inventoryItemsApi,
  InventoryItem,
  UpcLookupResult,
  INVENTORY_CATEGORIES,
  INVENTORY_UOMS,
  ALCOHOL_TYPES,
} from '../services/inventoryItemsService.ts';

type UpcStatus = 'idle' | 'checking' | 'duplicate' | 'not_found' | 'ready' | 'error';

const emptyForm = {
  sku: '',
  barcode: '',
  itemName: '',
  category: '',
  subCategory: '',
  brand: '',
  size: '',
  uom: '',
  alcoholType: '',
  activeFlag: true,
};

const InventoryItemsPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showError, showSuccess } = useSnackbar();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [upcStatus, setUpcStatus] = useState<UpcStatus>('idle');
  const [upcError, setUpcError] = useState('');
  const upcCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckedUpc = useRef('');
  const filledFromLookup = useRef(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);

  const fetchItems = async (pageNum: number = 1, searchValue: string = search) => {
    if (!sessionToken) return;

    setLoading(true);

    try {
      const response = await inventoryItemsApi.getItems(
        { page: pageNum, limit, search: searchValue.trim() || undefined },
        sessionToken
      );
      setItems(response.data);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch inventory items';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchItems(value);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchItems(1, value), 500);
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setUpcStatus('idle');
    setUpcError('');
    lastCheckedUpc.current = '';
    filledFromLookup.current = false;
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: InventoryItem) => {
    setEditingItem(item);
    setForm({
      sku: item.sku,
      barcode: item.barcode ?? '',
      itemName: item.itemName,
      category: item.category,
      subCategory: item.subCategory ?? '',
      brand: item.brand ?? '',
      size: item.size ?? '',
      uom: item.uom ?? '',
      alcoholType: item.alcoholType ?? '',
      activeFlag: item.activeFlag,
    });
    // The existing UPC is already known to be valid for this record
    setUpcStatus('ready');
    setUpcError('');
    lastCheckedUpc.current = (item.barcode ?? '').trim();
    filledFromLookup.current = false;
    setDialogOpen(true);
  };

  const handleDelete = async (item: InventoryItem) => {
    if (!sessionToken) return;

    if (window.confirm(`Are you sure you want to delete "${item.itemName}"?`)) {
      try {
        await inventoryItemsApi.deleteItem(item.id, sessionToken);
        showSuccess('Inventory item deleted successfully!');
        fetchItems(page);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete inventory item';
        showError(errorMessage);
      }
    }
  };

  const handleCloseDialog = () => {
    if (dialogLoading) return;
    if (upcCheckTimer.current) clearTimeout(upcCheckTimer.current);
    setDialogOpen(false);
  };

  // First check the inventory_items table for the entered/scanned UPC (no external
  // API call is made for known items), then pull item data from the lookup API to
  // populate the form. Fields stay editable, so the user can adjust or fill in the rest.
  const processUpc = useCallback(
    async (upc: string) => {
      const trimmed = upc.trim();
      if (!sessionToken || !trimmed) return;
      if (trimmed === lastCheckedUpc.current) return;
      // When editing, the record's own UPC is always valid - no checks needed
      if (editingItem && trimmed === (editingItem.barcode ?? '').trim()) {
        lastCheckedUpc.current = trimmed;
        setUpcStatus('ready');
        return;
      }
      lastCheckedUpc.current = trimmed;

      setUpcStatus('checking');
      // Clear data left over from a previous scan, but keep anything typed by hand
      if (filledFromLookup.current) {
        filledFromLookup.current = false;
        setForm((prev) => ({ ...emptyForm, barcode: prev.barcode, activeFlag: true }));
      }

      try {
        const exists = await inventoryItemsApi.upcExists(trimmed, sessionToken);
        if (exists) {
          setUpcStatus('duplicate');
          return;
        }

        const result: UpcLookupResult | null = await inventoryItemsApi.lookupUpc(trimmed, sessionToken);
        if (!result) {
          setUpcStatus('not_found');
          return;
        }

        setForm((prev) => ({
          ...prev,
          sku: result.sku,
          itemName: result.itemName ?? '',
          category: result.category ?? '',
          subCategory: result.subCategory ?? '',
          brand: result.brand ?? '',
          size: result.size ?? '',
          uom: result.uom ?? '',
          alcoholType: result.alcoholType ?? '',
        }));
        filledFromLookup.current = true;
        setUpcStatus('ready');
      } catch (err) {
        lastCheckedUpc.current = '';
        const errorMessage = err instanceof Error ? err.message : 'UPC lookup failed';
        setUpcError(errorMessage);
        setUpcStatus('error');
      }
    },
    [sessionToken, editingItem]
  );

  const handleUpcChange = (value: string) => {
    setForm((prev) => ({ ...prev, barcode: value }));
    setUpcStatus('idle');
    lastCheckedUpc.current = '';
    // Barcode scanners send an Enter key at the end (handled in onKeyDown);
    // the debounce below covers manual typing and scanners without an Enter suffix.
    if (upcCheckTimer.current) clearTimeout(upcCheckTimer.current);
    if (value.trim()) {
      upcCheckTimer.current = setTimeout(() => processUpc(value), 700);
    }
  };

  const handleUpcKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (upcCheckTimer.current) clearTimeout(upcCheckTimer.current);
      processUpc(form.barcode);
    }
  };

  const handleFieldChange = (field: keyof typeof emptyForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Save requires a UPC verified as unique ('ready' from a successful lookup, or
  // 'not_found' where the details were entered by hand) plus the required fields
  const canSave =
    (upcStatus === 'ready' || upcStatus === 'not_found') &&
    !!form.sku &&
    !!form.itemName &&
    !!form.category &&
    !dialogLoading;

  const handleSave = async () => {
    if (!sessionToken || !canSave) return;

    setDialogLoading(true);
    try {
      const payload = {
        sku: form.sku.trim(),
        barcode: form.barcode.trim() || undefined,
        itemName: form.itemName,
        category: form.category,
        subCategory: form.subCategory || undefined,
        brand: form.brand || undefined,
        size: form.size || undefined,
        uom: form.uom || undefined,
        alcoholType: form.alcoholType || undefined,
        activeFlag: form.activeFlag,
      };
      if (editingItem) {
        await inventoryItemsApi.updateItem(editingItem.id, payload, sessionToken);
        showSuccess('Inventory item updated successfully!');
      } else {
        await inventoryItemsApi.createItem(payload, sessionToken);
        showSuccess('Inventory item created successfully!');
      }
      setDialogOpen(false);
      fetchItems(page);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save inventory item';
      showError(errorMessage);
    } finally {
      setDialogLoading(false);
    }
  };

  const formatDate = (timestamp: string | number) => {
    const seconds = Number(timestamp);
    if (!seconds) return '-';
    return new Date(seconds * 1000).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Inventory Items</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => fetchItems(page)} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create
          </Button>
        </Box>
      </Box>

      <TextField
        value={search}
        onChange={(e) => handleSearchChange(e.target.value)}
        placeholder="Search by SKU, item name, or brand"
        size="small"
        sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Barcode</TableCell>
              <TableCell>Item Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Sub Category</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>UOM</TableCell>
              <TableCell>Alcohol Type</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Created</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 13 : 12} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 13 : 12} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No inventory items found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>{item.barcode || '-'}</TableCell>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.subCategory || '-'}</TableCell>
                  <TableCell>{item.brand || '-'}</TableCell>
                  <TableCell>{item.size || '-'}</TableCell>
                  <TableCell>{item.uom || '-'}</TableCell>
                  <TableCell>{item.alcoholType || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.activeFlag ? 'Active' : 'Inactive'}
                      color={item.activeFlag ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(item.createdDate)}</TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination page={page} count={totalPages} onChange={handlePageChange} color="primary" />
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
        {total} item{total === 1 ? '' : 's'} total
      </Typography>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Update Inventory Item' : 'Create Inventory Item'}</DialogTitle>
        <DialogContent>
          <Alert icon={<ScannerIcon />} severity="info" sx={{ mb: 2, mt: 1 }}>
            Click the UPC field and scan the barcode — the item details will be filled in
            automatically.
          </Alert>

          {upcStatus === 'duplicate' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This item already exists in inventory and cannot be added.
            </Alert>
          )}
          {upcStatus === 'not_found' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No item data found for this UPC. You can enter the details manually.
            </Alert>
          )}
          {upcStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {upcError}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="UPC / Barcode"
                value={form.barcode}
                onChange={(e) => handleUpcChange(e.target.value)}
                onKeyDown={handleUpcKeyDown}
                fullWidth
                autoFocus
                required
                disabled={dialogLoading}
                error={upcStatus === 'duplicate'}
                InputProps={{
                  endAdornment: upcStatus === 'checking' ? <CircularProgress size={20} /> : null,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SKU"
                value={form.sku}
                onChange={(e) => handleFieldChange('sku', e.target.value)}
                fullWidth
                required
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Item Name"
                value={form.itemName}
                onChange={(e) => handleFieldChange('itemName', e.target.value)}
                fullWidth
                required
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category"
                value={form.category}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                fullWidth
                required
                select
                disabled={dialogLoading}
              >
                {INVENTORY_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sub Category"
                value={form.subCategory}
                onChange={(e) => handleFieldChange('subCategory', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Brand"
                value={form.brand}
                onChange={(e) => handleFieldChange('brand', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Size"
                value={form.size}
                onChange={(e) => handleFieldChange('size', e.target.value)}
                fullWidth
                disabled={dialogLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="UOM"
                value={form.uom}
                onChange={(e) => handleFieldChange('uom', e.target.value)}
                fullWidth
                select
                disabled={dialogLoading}
              >
                <MenuItem value="">None</MenuItem>
                {INVENTORY_UOMS.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Alcohol Type"
                value={form.alcoholType}
                onChange={(e) => handleFieldChange('alcoholType', e.target.value)}
                fullWidth
                select
                disabled={dialogLoading}
              >
                <MenuItem value="">None</MenuItem>
                {ALCOHOL_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activeFlag}
                    onChange={(e) => handleFieldChange('activeFlag', e.target.checked)}
                    disabled={dialogLoading}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={dialogLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {dialogLoading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryItemsPage;
